import { useCallback, memo, useMemo } from "react";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useFiltersContext } from "../../../pages/Workspace/UserFilters/FiltersContext";
import { RangeFilter } from "../Ranges/RangeFilter";
import { useRangeFilter } from "../Ranges/hooks/useRangeFilter";
import { FilterRange } from "../Ranges/types";
import { useInsideFilterChip } from "../../../pages/Workspace/UserFilters/FiltersChips/hooks/useInsideFilterChip";
import { FilterLayout } from "./FilterLayout";

export const RevenueRangeFilter = memo(() => {
  const { draftFilters, setDraftFilters } = useFiltersContext();
  const { insideChip, elementRef } = useInsideFilterChip();

  // Get initial values from draftFilters
  const initialRange = useMemo<FilterRange>(
    () =>
      ({
        min: draftFilters?.revenue?.min,
        max: draftFilters?.revenue?.max,
      }) as FilterRange,
    [draftFilters?.revenue?.min, draftFilters?.revenue?.max]
  );

  // Handle revenue range change
  const handleRevenueRangeChange = useCallback(
    (newRange: FilterRange) => {
      setDraftFilters((prevFilters) => {
        if (newRange.min === undefined && newRange.max === undefined) {
          return {
            ...prevFilters,
            revenue: undefined,
            hasFixedRevenue: undefined,
          };
        }

        return {
          ...prevFilters,
          revenue: {
            min: newRange.min,
            max: newRange.max,
          },
        };
      });
    },
    [setDraftFilters]
  );

  // Handle fixed revenue change
  const handleFixedRevenueChange = useCallback(
    (hasFixed: boolean) => {
      setDraftFilters((prevFilters) => ({
        ...prevFilters,
        hasFixedRevenue: hasFixed || undefined,
      }));
    },
    [setDraftFilters]
  );

  // Use the range filter hook
  const {
    range,
    isFixed: hasFixedRevenue,
    rangeHasValues,
    handleRangeChange,
    handleFixedValueChange: handleFixedRevenueCheckChange,
    handleClearFilter,
  } = useRangeFilter({
    initialRange,
    hasFixedValue: !!draftFilters?.hasFixedRevenue,
    onRangeChange: handleRevenueRangeChange,
    onFixedValueChange: handleFixedRevenueChange,
  });

  return (
    <FilterLayout
      title="Revenue Range"
      onClear={handleClearFilter}
      showHeader={!insideChip}
      refProp={elementRef}
      id="revenue-range-filter"
    >
      <RangeFilter
        value={range}
        onChange={(min, max) => handleRangeChange({ min, max })}
      />

      <Stack direction="row" my={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={hasFixedRevenue}
              onChange={handleFixedRevenueCheckChange}
              disabled={!rangeHasValues}
            />
          }
          label="Limit to companies with fixed revenue values within this range"
        />
      </Stack>
    </FilterLayout>
  );
});
